/**
 * DriveMapDialog.jsx — Embedded Google Map showing a single drive's start → end
 * with driving directions. Uses @react-google-maps/api + VITE_GOOGLE_PLACES_KEY.
 *
 * Also exposes quick classify (business/personal) and driver-assign controls so
 * users can review a drive without leaving the map.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, IconButton, Box, Typography,
  Stack, Chip, Button, CircularProgress, Alert,
  Select, MenuItem, FormControl, InputLabel, Divider,
} from '@mui/material';
import {
  Close as CloseIcon,
  OpenInNew as OpenInNewIcon,
  Business as BusinessIcon,
  Person as PersonalIcon,
  CheckCircle as ConfirmedIcon,
} from '@mui/icons-material';
import { GoogleMap, useJsApiLoader, DirectionsRenderer, Marker, Polyline } from '@react-google-maps/api';
import { useMobile } from '../../hooks/useMobile';
import { useRoster } from '../../hooks/useRoster';
import { useClassifyDrive, useAssignDriver, useDrivePath } from '../../hooks/useTessie';

const MAPS_API_KEY = import.meta.env.VITE_GOOGLE_PLACES_KEY;
const LIBRARIES = ['places'];

function fmtTime(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}
function fmtDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}
function fmtMiles(m) {
  if (m == null) return '—';
  return `${parseFloat(m).toFixed(1)} mi`;
}

function buildExternalUrl(drive) {
  const hasStart = drive.startLat != null && drive.startLng != null;
  const hasEnd = drive.endLat != null && drive.endLng != null;
  const origin = hasStart ? `${drive.startLat},${drive.startLng}` : (drive.startLocation || '');
  const destination = hasEnd ? `${drive.endLat},${drive.endLng}` : (drive.endLocation || '');
  if (!origin && !destination) return null;
  if (!destination || !origin) {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(origin || destination)}`;
  }
  const params = new URLSearchParams({ api: '1', origin, destination, travelmode: 'driving' });
  return `https://www.google.com/maps/dir/?${params.toString()}`;
}

export default function DriveMapDialog({ open, onClose, drive }) {
  const { isMobile } = useMobile();
  const { data: roster = [] } = useRoster();
  const classifyMut = useClassifyDrive();
  const assignDriverMut = useAssignDriver();

  const { isLoaded, loadError } = useJsApiLoader({
    id: 'rfos-google-maps',
    googleMapsApiKey: MAPS_API_KEY || '',
    libraries: LIBRARIES,
  });

  const [directions, setDirections] = useState(null);
  const [dirError, setDirError] = useState(null);
  const mapRef = useRef(null);

  const hasStart = drive && drive.startLat != null && drive.startLng != null;
  const hasEnd = drive && drive.endLat != null && drive.endLng != null;

  const startPos = hasStart ? { lat: parseFloat(drive.startLat), lng: parseFloat(drive.startLng) } : null;
  const endPos = hasEnd ? { lat: parseFloat(drive.endLat), lng: parseFloat(drive.endLng) } : null;

  // Real driven path from Tessie (lat/lng points in time order)
  const { data: pathPoints = [], isLoading: pathLoading, error: pathError } = useDrivePath(
    drive?.id,
    open,
  );
  const hasRealPath = pathPoints.length >= 2;

  const center = useMemo(() => {
    if (startPos && endPos) {
      return { lat: (startPos.lat + endPos.lat) / 2, lng: (startPos.lng + endPos.lng) / 2 };
    }
    return startPos || endPos || { lat: 34.4208, lng: -119.6982 };
  }, [startPos, endPos]);

  useEffect(() => {
    if (!open) {
      setDirections(null);
      setDirError(null);
    }
  }, [open, drive?.id]);

  // Only ask Google Directions if we have NO real path (fallback)
  useEffect(() => {
    if (!open || !isLoaded || !startPos || !endPos) return;
    if (directions || hasRealPath || pathLoading) return;
    try {
      const svc = new window.google.maps.DirectionsService();
      svc.route(
        {
          origin: startPos,
          destination: endPos,
          travelMode: window.google.maps.TravelMode.DRIVING,
        },
        (result, status) => {
          if (status === 'OK') {
            setDirections(result);
          } else {
            setDirError(`Directions unavailable (${status})`);
          }
        },
      );
    } catch (e) {
      setDirError(e.message || 'Failed to load directions');
    }
  }, [open, isLoaded, startPos, endPos, directions, hasRealPath, pathLoading]);

  // Fit map bounds to whichever data we have
  useEffect(() => {
    if (!isLoaded || !mapRef.current) return;
    if (!hasRealPath && !startPos && !endPos) return;
    const bounds = new window.google.maps.LatLngBounds();
    if (hasRealPath) {
      pathPoints.forEach((p) => bounds.extend(p));
    } else {
      if (startPos) bounds.extend(startPos);
      if (endPos) bounds.extend(endPos);
    }
    mapRef.current.fitBounds(bounds, 48);
  }, [isLoaded, hasRealPath, pathPoints, startPos, endPos]);

  const handleMapLoad = (map) => {
    mapRef.current = map;
  };

  const externalUrl = drive ? buildExternalUrl(drive) : null;
  const noCoords = !startPos && !endPos;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      fullScreen={isMobile}
      PaperProps={{ sx: { height: isMobile ? '100%' : '80vh' } }}
    >
      <DialogTitle sx={{ pr: 6, py: 1.25 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="subtitle1" fontWeight={600} noWrap>
              {drive ? `${fmtDate(drive.startedAt)} · ${fmtTime(drive.startedAt)}–${fmtTime(drive.endedAt)}` : 'Drive map'}
            </Typography>
            {drive && (
              <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                {drive.startLocation || '—'} → {drive.endLocation || '—'}
              </Typography>
            )}
          </Box>
          <Stack direction="row" spacing={0.5} alignItems="center">
            {drive && (
              <Chip size="small" label={fmtMiles(drive.distanceMiles)} variant="outlined" />
            )}
            {drive?.tag === 'business' && (
              <Chip size="small" icon={<BusinessIcon />} label="Business" color="success" variant="outlined" />
            )}
            {drive?.tag === 'personal' && (
              <Chip size="small" icon={<PersonalIcon />} label="Personal" color="error" variant="outlined" />
            )}
          </Stack>
        </Stack>
        <IconButton
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
          aria-label="Close"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
        {!MAPS_API_KEY ? (
          <Alert severity="error" sx={{ m: 2 }}>
            Google Maps API key missing (VITE_GOOGLE_PLACES_KEY).
          </Alert>
        ) : loadError ? (
          <Alert severity="error" sx={{ m: 2 }}>
            Failed to load Google Maps: {loadError.message}
          </Alert>
        ) : noCoords ? (
          <Alert severity="warning" sx={{ m: 2 }}>
            No GPS coordinates on this drive. Try opening in Google Maps.
          </Alert>
        ) : !isLoaded ? (
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ flex: 1, position: 'relative', minHeight: 320 }}>
            <GoogleMap
              mapContainerStyle={{ width: '100%', height: '100%' }}
              center={center}
              zoom={13}
              onLoad={handleMapLoad}
              options={{
                streetViewControl: false,
                mapTypeControl: false,
                fullscreenControl: !isMobile,
                clickableIcons: false,
              }}
            >
              {hasRealPath ? (
                <>
                  {startPos && <Marker position={startPos} label="A" />}
                  {endPos && <Marker position={endPos} label="B" />}
                  <Polyline
                    path={pathPoints}
                    options={{
                      strokeColor: '#1976d2',
                      strokeOpacity: 0.9,
                      strokeWeight: 4,
                    }}
                  />
                </>
              ) : directions ? (
                <DirectionsRenderer
                  directions={directions}
                  options={{ suppressMarkers: false, preserveViewport: false }}
                />
              ) : (
                <>
                  {startPos && <Marker position={startPos} label="A" />}
                  {endPos && <Marker position={endPos} label="B" />}
                  {startPos && endPos && (
                    <Polyline
                      path={[startPos, endPos]}
                      options={{
                        strokeColor: '#1976d2',
                        strokeOpacity: 0.6,
                        strokeWeight: 3,
                        geodesic: true,
                      }}
                    />
                  )}
                </>
              )}
            </GoogleMap>
            {/* Status banner: real path > directions > straight line > error */}
            {pathLoading && !hasRealPath && (
              <Box sx={{ position: 'absolute', top: 8, left: 8, bgcolor: 'background.paper', px: 1, py: 0.5, borderRadius: 1, boxShadow: 1, display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <CircularProgress size={14} />
                <Typography variant="caption">Loading GPS path…</Typography>
              </Box>
            )}
            {hasRealPath && (
              <Box sx={{ position: 'absolute', bottom: 8, left: 8, bgcolor: 'background.paper', px: 1, py: 0.5, borderRadius: 1, boxShadow: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  Actual GPS path · {pathPoints.length} points
                </Typography>
              </Box>
            )}
            {!hasRealPath && !pathLoading && pathError && (
              <Alert severity="warning" sx={{ position: 'absolute', bottom: 8, left: 8, right: 8 }}>
                Couldn’t load GPS path from Tessie. {directions ? 'Showing road-routed estimate.' : 'Showing straight line.'}
              </Alert>
            )}
            {!hasRealPath && !pathLoading && !pathError && dirError && (
              <Alert severity="info" sx={{ position: 'absolute', bottom: 8, left: 8, right: 8 }}>
                {dirError === 'Directions unavailable (REQUEST_DENIED)'
                  ? 'Directions API not enabled — showing straight-line route.'
                  : `${dirError} — showing straight-line route.`}
              </Alert>
            )}
          </Box>
        )}

        {drive && (
          <Box sx={{ p: 1.25, borderTop: '1px solid', borderColor: 'divider' }}>
            <Stack
              direction={isMobile ? 'column' : 'row'}
              spacing={1}
              alignItems={isMobile ? 'stretch' : 'center'}
              sx={{ flexWrap: 'wrap', gap: 1 }}
            >
              <FormControl size="small" sx={{ minWidth: 160, flex: isMobile ? 1 : 'unset' }}>
                <InputLabel>Driver</InputLabel>
                <Select
                  label="Driver"
                  value={drive.rosterId || ''}
                  onChange={(e) => assignDriverMut.mutate({ id: drive.id, rosterId: e.target.value })}
                  disabled={assignDriverMut.isPending}
                  displayEmpty
                >
                  <MenuItem value="" disabled><em>Assign driver</em></MenuItem>
                  {roster.map((r) => (
                    <MenuItem key={r.id} value={r.id}>
                      <Stack direction="row" spacing={0.75} alignItems="center">
                        {r.accentColor && (
                          <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: r.accentColor }} />
                        )}
                        <span>{r.name}</span>
                      </Stack>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Stack direction="row" spacing={1} sx={{ flex: 1 }}>
                <Button
                  fullWidth={isMobile}
                  size="small"
                  variant={drive.tag === 'business' ? 'contained' : 'outlined'}
                  color="success"
                  startIcon={<BusinessIcon />}
                  onClick={() => classifyMut.mutate({ id: drive.id, tag: 'business' })}
                  disabled={classifyMut.isPending}
                  sx={{ minHeight: 40 }}
                >
                  Business
                </Button>
                <Button
                  fullWidth={isMobile}
                  size="small"
                  variant={drive.tag === 'personal' ? 'contained' : 'outlined'}
                  color="error"
                  startIcon={<PersonalIcon />}
                  onClick={() => classifyMut.mutate({ id: drive.id, tag: 'personal' })}
                  disabled={classifyMut.isPending}
                  sx={{ minHeight: 40 }}
                >
                  Personal
                </Button>
              </Stack>

              {drive.confirmed && (
                <Chip
                  size="small"
                  icon={<ConfirmedIcon />}
                  label={`Confirmed${drive.confirmedBy ? ` · ${drive.confirmedBy}` : ''}`}
                  color="success"
                  variant="outlined"
                />
              )}
            </Stack>

            {externalUrl && (
              <>
                <Divider sx={{ my: 1 }} />
                <Button
                  size="small"
                  startIcon={<OpenInNewIcon />}
                  href={externalUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  fullWidth={isMobile}
                >
                  Open in Google Maps
                </Button>
              </>
            )}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}
